import { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, GraduationCap, Sparkles, Wallet, Users,
  Settings, Shield, Bell, Menu, X, LogOut, Zap, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Logo } from "@/components/Logo";
import { ThemeToggle, LanguageToggle } from "@/components/ThemeLangControls";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/work", icon: Briefcase, key: "work" },
  { to: "/learn", icon: GraduationCap, key: "learn" },
  { to: "/create", icon: Sparkles, key: "create" },
  { to: "/money", icon: Wallet, key: "money" },
  { to: "/community", icon: Users, key: "community" },
];

function NavItem({ item, t, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} onClick={onClick} data-testid={`nav-${item.key}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
          isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`}>
      <Icon className="h-5 w-5 shrink-0" />
      <span>{t(`nav.${item.key}`)}</span>
    </NavLink>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const load = async () => {
    try {
      const { data } = await api.get("/users/me/notifications");
      setItems(data.items); setUnread(data.unread);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);
  const markAll = async () => { await api.post("/users/me/notifications/read-all"); load(); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <span className="font-display font-semibold">Notifications</span>
          {unread > 0 && <button onClick={markAll} className="text-xs text-primary" data-testid="mark-all-read">Tout lire</button>}
        </div>
        <ScrollArea className="max-h-80">
          {items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">—</div>}
          {items.map((n) => (
            <div key={n.id} className={`p-3 border-b text-sm ${!n.is_read ? "bg-muted/50" : ""}`}>
              <div className="font-medium">{n.title}</div>
              {n.body && <div className="text-muted-foreground text-xs mt-0.5">{n.body}</div>}
            </div>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const initials = (user?.profile?.full_name || user?.email || "U").slice(0, 2).toUpperCase();
  const isAdmin = ["ADMIN", "SUPER_ADMIN", "MODERATOR"].includes(user?.role);

  const SidebarContent = ({ onNav }) => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5">
        <Logo onClick={() => navigate("/dashboard")} className="cursor-pointer" />
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {NAV.map((item) => <NavItem key={item.key} item={item} t={t} onClick={onNav} />)}
        <div className="pt-3 mt-3 border-t space-y-1">
          <NavItem item={{ to: "/documents", icon: FileText, key: "documents" }} t={t} onClick={onNav} />
          <NavItem item={{ to: "/settings", icon: Settings, key: "settings" }} t={t} onClick={onNav} />
          {isAdmin && <NavItem item={{ to: "/admin", icon: Shield, key: "admin" }} t={t} onClick={onNav} />}
        </div>
      </nav>
      <div className="p-3">
        <div className="rounded-xl bg-muted p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Zap className="h-4 w-4 text-primary" />
            {user && user.credits != null ? (
              <span className="font-semibold" data-testid="sidebar-credits">{user.credits}</span>
            ) : (
              <span className="h-4 w-8 rounded bg-muted-foreground/20 animate-pulse inline-block" data-testid="sidebar-credits-loading" />
            )}
            <span className="text-muted-foreground text-xs">{t("common.credits")}</span>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={() => navigate("/pricing")} data-testid="sidebar-upgrade">
            {t("dashboard.upgrade")}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r flex-col fixed inset-y-0 bg-card">
        <SidebarContent />
      </aside>

      <div className="flex-1 lg:pl-64 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-16 border-b bg-card/70 backdrop-blur-xl flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <SidebarContent onNav={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <Logo className="lg:hidden" onClick={() => navigate("/dashboard")} />
          </div>
          <div className="flex items-center gap-1">
            <LanguageToggle />
            <ThemeToggle />
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full h-9 w-9 p-0 bg-primary/10 text-primary font-semibold" data-testid="user-menu-trigger">
                  {initials}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium truncate">{user?.profile?.full_name || user?.email}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">{t("nav.settings")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-pricing">{t("nav.pricing")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout" className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />{t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 h-16 bg-card/90 backdrop-blur-xl border-t flex items-center justify-around px-1">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink key={item.key} to={item.to} data-testid={`bottomnav-${item.key}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-[10px] font-medium ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}>
              <Icon className="h-5 w-5" />
              <span>{t(`nav.${item.key}`)}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
